import { useCallback, useRef } from "react";

interface ResizeHandleProps {
  onResize: (delta: number) => void;
  direction?: "horizontal" | "vertical";
}

export function ResizeHandle({ onResize, direction = "horizontal" }: ResizeHandleProps) {
  const isDragging = useRef(false);
  const startPos = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      startPos.current = direction === "horizontal" ? e.clientX : e.clientY;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current) return;
        const current = direction === "horizontal" ? moveEvent.clientX : moveEvent.clientY;
        const delta = current - startPos.current;
        startPos.current = current;
        onResize(delta);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = direction === "horizontal" ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
    },
    [onResize, direction]
  );

  // Touch support
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      isDragging.current = true;
      startPos.current = direction === "horizontal" ? e.touches[0].clientX : e.touches[0].clientY;

      const handleTouchMove = (moveEvent: TouchEvent) => {
        if (!isDragging.current) return;
        const current = direction === "horizontal" ? moveEvent.touches[0].clientX : moveEvent.touches[0].clientY;
        const delta = current - startPos.current;
        startPos.current = current;
        onResize(delta);
      };

      const handleTouchEnd = () => {
        isDragging.current = false;
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };

      document.addEventListener("touchmove", handleTouchMove, { passive: true });
      document.addEventListener("touchend", handleTouchEnd);
    },
    [onResize, direction]
  );

  if (direction === "vertical") {
    return (
      <div
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className="h-[6px] shrink-0 cursor-row-resize group flex items-center justify-center relative"
      >
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-[var(--lilia-border)]" />
        <div className="w-8 h-1 rounded-full bg-[var(--lilia-border)] group-hover:bg-[var(--lilia-primary,#0A76A4)] transition-colors z-10" />
      </div>
    );
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className="w-[6px] shrink-0 cursor-col-resize group flex items-center justify-center relative"
    >
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-[var(--lilia-border)]" />
      <div className="h-8 w-1 rounded-full bg-[var(--lilia-border)] group-hover:bg-[var(--lilia-primary,#0A76A4)] transition-colors z-10" />
    </div>
  );
}
